import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import Button from "../../components/atoms/button/Button";
import ReusableModal from "../../components/atoms/modal/ReusableModal";
import SimpleTable from "../../components/atoms/table/simpleTable/simpleTable";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./companyManagement.css";
import { Container } from "react-bootstrap";
import {
  addCompanyData,
  getCompanyListData,
  addLocationData,
  editCompanyData,
  editLocationData,
  getSalesRepData,
  getCountriesWithStates,
  getCustomerListData,
  editCustomerData,
  approveUser,
} from "./companyManagementApi";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import CustomerManagement from "./CustomerManagement";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getCompanyFields, getLocationFields } from "./fields/formFields";
import DynamicForm from "../../components/atoms/dynamicForm";
import SecondTab from "../../components/organisms/secondTab";
import PageDescriptionBar from "../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const CompanyManagement = ({ TabConfig, isAnalyst }) => {
  const userData = JSON.parse(localStorage.getItem("userData")) || {};
  const userId = userData.userID || 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const routeLocation = useLocation();
  const [activeTab, setActiveTab] = useState(
    routeLocation.state?.tab === "Customer" || searchParams.get("tab") === "Customer" ? "Customer" : "Company"
  );
  const [openAddSample, setOpenAddSample] = useState(
    routeLocation.state?.openAddSample === true || searchParams.get("openAddSample") === "true"
  );

  // Sync openAddSample state when navigating via state (e.g. same-route navigation)
  useEffect(() => {
    if (routeLocation.state?.openAddSample === true) {
      setOpenAddSample(true);
      setActiveTab("Customer");
    } else if (searchParams.get("openAddSample") === "true") {
      setOpenAddSample(true);
      setActiveTab("Customer");
    }
  }, [routeLocation.state, searchParams]);
  const [expandedCompanyId, setExpandedCompanyId] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [companyStatusFilter, setCompanyStatusFilter] = useState("Active");
  const [customerStatusFilter, setCustomerStatusFilter] = useState("Active");
  const [companyList, setCompanyList] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loadingAllCompanies, setLoadingAllCompanies] = useState(false);
  const [phibroRepOptions, setPhibroRepOptions] = useState([]);
  const [countryStateList, setCountryStateList] = useState([]);
  const [paginationData, setPaginationData] = useState({});

  const [companyForm, setCompanyForm] = useState({
    companyName: "",
    country: "",
    state: "",
    city: "",
    companyId: "",
  });
  const [locationForm, setLocationForm] = useState({
    companyName: "",
    location: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zip: "",
    phone: "",
    email: "",
    locationId: "",
    companyId: "",
  });

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandAll, setExpandAll] = useState(false);
  const [expandedLocationIds, setExpandedLocationIds] = useState([]);
  const [locationCustomers, setLocationCustomers] = useState({});

  const formRef = useRef();

  useEffect(() => {
    fetchCompanies();
  }, [search, companyStatusFilter]);

  useEffect(() => {
    const minLength = 1;
    const delay = 1000; // ms
    const handler = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= minLength) {
        setCurrentPage(1);
        setSearch(searchInput);
      }
      // otherwise do nothing until user types more or presses Enter
    }, delay);

    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const fetchCountriesStates = async () => {
      try {
        const res = await getCountriesWithStates();
        setCountryStateList(Array.isArray(res) ? res : []);
      } catch {
        setCountryStateList([]);
      }
    };
    fetchCountriesStates();
  }, []);

  useEffect(() => {
    const fetchSalesReps = async () => {
      try {
        const res = await getSalesRepData();
        setPhibroRepOptions(Array.isArray(res) ? res : []);
      } catch {
        setPhibroRepOptions([]);
      }
    };
    fetchSalesReps();
  }, []);

  // When Add Location modal opens, fetch the full company list so the
  // Company dropdown in the location form can show all companies.
  useEffect(() => {
    let active = true;
    const fetchAllCompanies = async () => {
      if (!showLocationModal) return;
      if (allCompanies && allCompanies.length > 0) return; // already loaded

      setLoadingAllCompanies(true);
      try {
        const pageSize = 500;
        let page = 1;
        let accumulated = [];
        while (true) {
          const res = await getCompanyListData({ PageNumber: page, PageSize: pageSize });
          if (!res) break;
          const pageResults = Array.isArray(res.results) ? res.results : [];
          accumulated = accumulated.concat(pageResults);
          const total = Number(res.totalRecords) || accumulated.length;
          if (accumulated.length >= total || pageResults.length === 0) break;
          page += 1;
        }
        if (active) setAllCompanies(accumulated);
      } catch (error) {
        console.error("Failed to fetch full company list for location modal:", error);
      } finally {
        if (active) setLoadingAllCompanies(false);
      }
    };

    fetchAllCompanies();

    return () => {
      active = false;
    };
  }, [showLocationModal, allCompanies]);

  const prevCompanyCountryRef = useRef(companyForm.country);
  const prevLocationCountryRef = useRef(locationForm.country);

  useEffect(() => {
    if (
      prevCompanyCountryRef.current &&
      companyForm.country &&
      prevCompanyCountryRef.current !== companyForm.country
    ) {
      setCompanyForm((prev) => ({ ...prev, state: "" }));
    }
    prevCompanyCountryRef.current = companyForm.country;
  }, [companyForm.country]);

  useEffect(() => {
    if (
      prevLocationCountryRef.current &&
      locationForm.country &&
      prevLocationCountryRef.current !== locationForm.country
    ) {
      setLocationForm((prev) => ({ ...prev, state: "" }));
    }
    prevLocationCountryRef.current = locationForm.country;
  }, [locationForm.country]);

  const fetchCompanies = async (searchtext, currentPage, rowsPerPage) => {
    setCompanyLoading(true);
    try {
      const res = await getCompanyListData({
        PageNumber: currentPage,
        PageSize: rowsPerPage,
        ...(search && { FilterText: search }),
        Status: companyStatusFilter,
      });
      const { totalRecords, pageNumber, pageSize, totalPages } = res;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      setCompanyList(Array.isArray(res.results) ? res.results : []);
    } catch (error) {
      setCompanyList([]);
    }
    setCompanyLoading(false);
  };

  const handleExpand = () => {
    if (!expandAll) {

      setExpandedCompanyId(companyList.map((c) => c.companyId));
      setExpandAll(true);
    } else {
      // Collapse all: empty array
      setExpandedCompanyId([]);
      setExpandAll(false);
    }
  };
  const handleRowExpandToggle = (companyId) => {
    setExpandedCompanyId((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const fetchCustomersForLocation = async (locationId, locationName) => {
    setLocationCustomers((prev) => ({
      ...prev,
      [locationId]: { data: prev[locationId]?.data || [], loading: true },
    }));
    try {
      const res = await getCustomerListData({
        PageNumber: 1,
        PageSize: 500,
        ...(locationName && { FilterText: locationName }),
      });
      const customers = Array.isArray(res.results)
        ? res.results
            .filter((item) => String(item.locationId) === String(locationId))
            .map((item) => ({
              companyName: item.companyName,
              location: item.locationName,
              customerName: item.customerName,
              email: item.email,
              historicalData: item.showHistoricalData,
              locationAdmin: item.isLocationAdmin,
              actionSlider: item.isActive,
              customerId: item.customerId,
              salesRepId: item.phiBroRepName,
              companyId: item.companyId,
              locationId: item.locationId,
              actualSalesRepId: item.salesRepId || item.phiBroRepId,
              isUpdated: item.isUpdated,
            }))
        : [];
      setLocationCustomers((prev) => ({
        ...prev,
        [locationId]: { data: customers, loading: false },
      }));
    } catch {
      setLocationCustomers((prev) => ({
        ...prev,
        [locationId]: { data: [], loading: false },
      }));
    }
  };

  const handleLocationExpandToggle = (locationId, locationName) => {
    setExpandedLocationIds((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      } else {
        if (!locationCustomers[locationId]?.data?.length) {
          fetchCustomersForLocation(locationId, locationName);
        }
        return [...prev, locationId];
      }
    });
  };

  const handleCustomerToggle = async (row, apiKey, newValue) => {
    if (!row.isUpdated) {
      let msg = "";
      if (apiKey === "showHistoricalData")
        msg = "Can't update Historical Data for new Customer.";
      else if (apiKey === "isLocationAdmin")
        msg = "Can't update Location Admin for new Customer.";
      else if (apiKey === "isActive")
        msg = "Can't update User Status for new Customer.";
      if (msg) toast.error(msg);
      return;
    }
    if (apiKey === "showHistoricalData" || apiKey === "isLocationAdmin") {
      const nameParts = (row.customerName || "").split(" ");
      const firstName = nameParts[0] || "";
      const lastName =
        nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
      const payload = {
        role: row.salesRepId || "",
        firstName,
        lastName,
        email: row.email || "",
        location: row.location || "",
        customerId: row.customerId || "",
        showHistoricalData:
          apiKey === "showHistoricalData" ? newValue : !!row.historicalData,
        isLocationAdmin:
          apiKey === "isLocationAdmin" ? newValue : !!row.locationAdmin,
        isActive: apiKey === "isActive" ? newValue : !!row.actionSlider,
        isDeleted: false,
      };
      try {
        await editCustomerData(payload);
        toast.success("Customer status updated.");
        fetchCustomersForLocation(row.locationId, row.location);
      } catch {
        toast.error("Failed to update customer status");
      }
    } else {
      try {
        await approveUser(row.customerId, newValue);
        toast.success("Customer status updated.");
        fetchCustomersForLocation(row.locationId, row.location);
      } catch {
        toast.error("Failed to update customer status");
      }
    }
  };

  const customerColumns = [
    { header: "Customer Name", accessor: "customerName" },
    { header: "Email", accessor: "email" },
    {
      header: "Historical Data",
      accessor: "historicalData",
      disableSort: true,
    },
    {
      header: "Location Admin",
      accessor: "locationAdmin",
      disableSort: true,
    },
    {
      header: "User Status",
      accessor: "actionSlider",
      disableSort: true,
    },
  ];

  const companyColumns = [
    { header: "Company", accessor: "name" },
    { header: "Address", accessor: "address" },
    { header: "Country", accessor: "country" },
    { header: "Phone", accessor: "phone" },
    {
      header: "Action",
      accessor: "action",
      disableSort: true,
      cell: (row) => (
        <div>
          <Button
            size="sm"
            variant="edit-disabled"
            title="Edit company"
            onClick={() => {
              setEditMode("company");
              setCompanyForm({
                companyName: row.name || "",
                country: row.country || "",
                state: row.state || "",
                city: row.city || "",
                companyId: row.companyId || "",
              });
              setShowCompanyModal(true);
            }}
          >
            Edit
          </Button>
        </div>
      ),
    },
    {
      header: (
        <Button onClick={handleExpand} title={expandAll ? "Collapse all rows" : "Expand all rows"}>
          {expandAll ? "Collapse All" : "Expand All"}
        </Button>
      ),
      accessor: "expand",
      disableSort: true,
      cell: (row) => (
        <div className="d-flex justify-content-center">
          <div
            onClick={() => {
              handleRowExpandToggle(row.companyId);
            }}
            style={{
              cursor: "pointer",
            }}
            aria-label={
              expandedCompanyId.includes(row.companyId)
                ? "Collapse details"
                : "Expand details"
            }
            title={
              expandedCompanyId.includes(row.companyId)
                ? "Collapse details"
                : "Expand details"
            }
          >
            <FaChevronDown
              size={14}
              style={{
                transition: "transform .2s",
                transform: expandedCompanyId.includes(row.companyId)
                  ? "rotate(180deg)"
                  : "none",
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  const locationColumns = [
    { header: "Location", accessor: "name" },
    { header: "State", accessor: "state" },
    { header: "City", accessor: "city" },
    { header: "Zip", accessor: "zip" },
    {
      header: "Active",
      accessor: "action",
      disableSort: true,
      cell: (loc) => (
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="edit-active"
            title="Edit location"
            onClick={() => {
              setEditMode("location");
              setLocationForm({
                companyName:
                  loc.companyName ||
                  companyList.find((c) => c.companyId === loc.companyId)
                    ?.name ||
                  "",
                companyId: loc.companyId || "",
                location: loc.name || "",
                address: loc.address || "",
                country: loc.country || "",
                state: loc.state || "",
                city: loc.city || "",
                zip: loc.zip || "",
                phone: loc.phone || "",
                email: loc.email || "",
                locationId: loc.locationId || "",
                fieldStaffId: loc.fieldStaffId || loc.salesRepId || "",
              });
              setShowLocationModal(true);
            }}
          >
            Edit
          </Button>
          <OverlayTrigger placement="top" overlay={<Tooltip>{loc.isActive ? "Deactivate location" : "Activate location"}</Tooltip>}>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!loc.isActive}
              onChange={async (e) => {
                const newValue = e.target.checked;
                const updatedLocation = {
                  companyName:
                    loc.companyName ||
                    companyList.find((c) => c.companyId === loc.companyId)
                      ?.name ||
                    "",
                  companyId: loc.companyId || "",
                  name: loc.name || "",
                  address: loc.address || "",
                  country: loc.country || "",
                  state: loc.state || "",
                  city: loc.city || "",
                  zip: loc.zip || "",
                  phone: loc.phone || "",
                  email: loc.email || "",
                  locationId: loc.locationId || "",
                  isActive: newValue,
                  status: newValue ? "active" : "inactive",
                };
                const currentExpandedId = expandedCompanyId;
                try {
                  await editLocationData(updatedLocation);
                  await fetchCompanies();
                  setExpandedCompanyId(currentExpandedId);
                  toast.success(
                    `Location status updated to ${
                      newValue ? "Active" : "Inactive"
                    }`
                  );
                } catch (error) {
                  // The write may have partially succeeded server-side even though
                  // the request errored, so resync the toggle with actual server state
                  // instead of leaving it showing stale data.
                  await fetchCompanies();
                  setExpandedCompanyId(currentExpandedId);
                  toast.error("Failed to update location status");
                }
              }}
            />
            <span
              className={`slider ${loc.isActive ? "roundOn" : "roundOff"}`}
            ></span>
          </label>
          </OverlayTrigger>
        </div>
      ),
    },
  ];

  const customRowRender = (row) => (
    <React.Fragment key={row.companyId}>
      <tr>
        <td>{row.name}</td>
        <td>{row.address}</td>
        <td>{row.country}</td>
        <td>{row.phone}</td>
        <td>
            <Button
              size="sm"
              variant="edit-disabled"
              title="Edit company"
              onClick={() => {
                setEditMode("company");
                setCompanyForm({
                  companyName: row.name || "",
                  country: row.country || "",
                  state: row.state || "",
                  city: row.city || "",
                  companyId: row.companyId || "",
                });
                setShowCompanyModal(true);
              }}
            >
              Edit
            </Button>
        </td>
        <td>
         {row.locations && row.locations.length > 0 && (
              <span
                style={{ cursor: "pointer", marginLeft: 30 }}
                onClick={() => handleRowExpandToggle(row.companyId)}
                title={expandedCompanyId.includes(row.companyId) ? "Collapse details" : "Expand details"}
              >
                {expandedCompanyId.includes(row.companyId) ? (
                  <FaChevronUp size={16} />
                ) : (
                  <FaChevronDown size={16} />
                )}
              </span>
            )}

        </td>
      </tr>
      {expandedCompanyId.includes(row.companyId) &&
        row.locations &&
        row.locations.length > 0 && (
            <tr>
              <td colSpan={companyColumns.length}>
              <div className="location-table-wrapper">
                <SimpleTable
                  columns={locationColumns}
                  data={row.locations}
                  showPagination={false}
                  customRowRender={(loc) => (
                    <React.Fragment key={loc.locationId}>
                      <tr>
                        <td>{loc.name}</td>
                        <td>{loc.state}</td>
                        <td>{loc.city}</td>
                        <td>{loc.zip}</td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Button
                              size="sm"
                              variant="edit-active"
                              title="Edit location"
                            onClick={() => {
                                setEditMode("location");
                                setLocationForm({
                                  companyName:
                                    loc.companyName ||
                                    companyList.find(
                                      (c) => c.companyId === loc.companyId
                                    )?.name ||
                                    "",
                                  companyId: loc.companyId || "",
                                  location: loc.name || "",
                                  address: loc.address || "",
                                  country: loc.country || "",
                                  state: loc.state || "",
                                  city: loc.city || "",
                                  zip: loc.zip || "",
                                  phone: loc.phone || "",
                                  email: loc.email || "",
                                  locationId: loc.locationId || "",
                                  fieldStaffId: loc.fieldStaffId || loc.salesRepId || "",
                                });
                                setShowLocationModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <OverlayTrigger placement="top" overlay={<Tooltip>{loc.isActive ? "Deactivate location" : "Activate location"}</Tooltip>}>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={!!loc.isActive}
                              onChange={async (e) => {
                                  const newValue = e.target.checked;
                                  const updatedLocation = {
                                    companyName:
                                      loc.companyName ||
                                      companyList.find(
                                        (c) => c.companyId === loc.companyId
                                      )?.name ||
                                      "",
                                    companyId: loc.companyId || "",
                                    address: loc.address || "",
                                    country: loc.country || "",
                                    state: loc.state || "",
                                    city: loc.city || "",
                                    zip: loc.zip || "",
                                    name: loc.name || "",
                                    phone: loc.phone || "",
                                    email: loc.email || "",
                                    locationId: loc.locationId || "",
                                    isActive: newValue,
                                    status: newValue ? "active" : "inactive",
                                  };
                                  const currentExpandedId = expandedCompanyId;
                                  try {
                                    await editLocationData(updatedLocation);
                                    await fetchCompanies();
                                    setExpandedCompanyId(currentExpandedId);
                                    toast.success(
                                      `Location status updated to ${
                                        newValue ? "Active" : "Inactive"
                                      }`
                                    );
                                  } catch (error) {
                                    toast.error(
                                      "Failed to update location status"
                                    );
                                  }
                                }}
                              />
                              <span
                                className={`slider ${
                                  loc.isActive ? "roundOn" : "roundOff"
                                }`}
                              ></span>
                            </label>
                            </OverlayTrigger>
                            {activeTab === "Customer" && (
                              <span
                                style={{ cursor: "pointer", marginLeft: 10 }}
                                onClick={() =>
                                  handleLocationExpandToggle(loc.locationId, loc.name)
                                }
                              >
                                {expandedLocationIds.includes(loc.locationId) ? (
                                  <FaChevronUp size={14} />
                                ) : (
                                  <FaChevronDown size={14} />
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {activeTab === "Customer" && expandedLocationIds.includes(loc.locationId) && (
                        <tr>
                          <td colSpan={locationColumns.length}>
                            <div className="customer-table-wrapper">
                              {locationCustomers[loc.locationId]?.loading ? (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "20px 0",
                                  }}
                                >
                                  <Spinner animation="border" size="sm" />{" "}
                                  Loading customers...
                                </div>
                              ) : locationCustomers[loc.locationId]?.data
                                  ?.length > 0 ? (
                                <SimpleTable
                                  columns={customerColumns}
                                  data={
                                    locationCustomers[loc.locationId]?.data || []
                                  }
                                  showPagination={false}
                                  customRowRender={(cust) => (
                                    <tr key={cust.customerId}>
                                      <td>{cust.customerName}</td>
                                      <td>{cust.email}</td>
                                      <td>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>{cust.historicalData ? "Disable historical data" : "Enable historical data"}</Tooltip>}>
                                        <label className="switch">
                                          <input
                                            type="checkbox"
                                            checked={!!cust.historicalData}
                                            onChange={(e) =>
                                              handleCustomerToggle(
                                                cust,
                                                "showHistoricalData",
                                                e.target.checked
                                              )
                                            }
                                          />
                                          <span
                                            className={`slider ${
                                              cust.historicalData
                                                ? "roundOn"
                                                : "roundOff"
                                            }`}
                                          ></span>
                                        </label>
                                        </OverlayTrigger>
                                      </td>
                                      <td>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>{cust.locationAdmin ? "Remove location admin" : "Make location admin"}</Tooltip>}>
                                        <label className="switch">
                                          <input
                                            type="checkbox"
                                            checked={!!cust.locationAdmin}
                                            onChange={(e) =>
                                              handleCustomerToggle(
                                                cust,
                                                "isLocationAdmin",
                                                e.target.checked
                                              )
                                            }
                                          />
                                          <span
                                            className={`slider ${
                                              cust.locationAdmin
                                                ? "roundOn"
                                                : "roundOff"
                                            }`}
                                          ></span>
                                        </label>
                                        </OverlayTrigger>
                                      </td>
                                      <td>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>{cust.actionSlider ? "Deactivate user" : "Activate user"}</Tooltip>}>
                                        <label className="switch">
                                          <input
                                            type="checkbox"
                                            checked={!!cust.actionSlider}
                                            onChange={(e) =>
                                              handleCustomerToggle(
                                                cust,
                                                "isActive",
                                                e.target.checked
                                              )
                                            }
                                          />
                                          <span
                                            className={`slider ${
                                              cust.actionSlider
                                                ? "roundOn"
                                                : "roundOff"
                                            }`}
                                          ></span>
                                        </label>
                                        </OverlayTrigger>
                                      </td>
                                    </tr>
                                  )}
                                />
                              ) : (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                    color: "#888",
                                    fontSize: 14,
                                  }}
                                >
                                  No customers found for this location.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )}
                />
              </div>
            </td>
          </tr>
        )}
    </React.Fragment>
  );

  const handleAddCompany = async () => {
    const newCompany = {
      companyId: companyForm.companyId || 0,
      name: companyForm.companyName,
      country: companyForm.country,
      state: companyForm.state,
      city: companyForm.city,
      isActive: true,
    };
    try {
      await addCompanyData(newCompany);
      toast.success("Company added successfully!");
      await fetchCompanies();
      return true;
    } catch (error) {
      console.error("Failed to add company:", error);      
      let msg = "Failed to add Company";
      if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          msg = error.response.data;
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      } else if (error?.message) {
        msg = error.message;
      }
      toast.error(msg);
      return false;
    }
  };

  const handleAddLocation = async () => {
    const newLocation = {
      companyName: locationForm.companyName,
      name: locationForm.location,
      address: locationForm.address,
      country: locationForm.country,
      state: locationForm.state,
      city: locationForm.city,
      zip: locationForm.zip,
      phone: locationForm.phone,
      email: locationForm.email,
      isActive: true,
      locationId: locationForm.locationId || 0,
      companyId: locationForm.companyId || "",
      status: "active",
      fieldStaffId: locationForm.fieldStaffId,
    };
    try {
      await addLocationData(newLocation);
      toast.success("Location added successfully!");
      await fetchCompanies();
    } catch (error) {
      console.error("Failed to add location:", error);
      let msg = "Failed to add Location";
      if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          msg = error.response.data;
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      } else if (error?.message) {
        msg = error.message;
      }
      toast.error(msg);
    }
  };

  const handleUpdateCompany = async () => {
    const payload = {
      companyId: companyForm.companyId,
      name: companyForm.companyName,
      country: companyForm.country,
      state: companyForm.state,
      city: companyForm.city,
      isActive: true,
    };
    try {
      await editCompanyData(payload);
      toast.success("Company edited successfully!");
      await fetchCompanies();
      return true;
    } catch (error) {
      console.error("Failed to update company:", error);
      let msg = "Failed to update Company";
      if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          msg = error.response.data;
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      } else if (error?.message) {
        msg = error.message;
      }
      toast.error(msg);
      return false;
    }
  };

  const handleUpdateLocation = async () => {
    const updatedLocation = {
      companyName: locationForm.companyName,
      name: locationForm.location,
      address: locationForm.address,
      country: locationForm.country,
      state: locationForm.state,
      city: locationForm.city,
      zip: locationForm.zip,
      phone: locationForm.phone,
      email: locationForm.email,
      isActive: true,
      locationId: locationForm.locationId || 0,
      companyId: locationForm.companyId || "",
      status: "active",
      fieldStaffId: locationForm.fieldStaffId,
    };
    try {
      await editLocationData(updatedLocation);
      toast.success("Location edited successfully!");
      await fetchCompanies();
    } catch (error) {
      console.error("Failed to update location:", error);
       let msg = "Failed to update Location";
      if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          msg = error.response.data;
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      } else if (error?.message) {
        msg = error.message;
      }
      toast.error(msg);
    }
  };

  const selectedCompanyLocations =
    companyList.find((c) => c.companyId === companyForm.companyId)?.locations ||
    [];

  const selectedCountryObj = countryStateList.find(
    (c) => c.country === companyForm.country
  );
  const companyStateOptions = selectedCountryObj
    ? selectedCountryObj.states
    : [];

  const selectedLocationCountryObj = countryStateList.find(
    (c) => c.country === locationForm.country
  );
  const locationStateOptions = selectedLocationCountryObj
    ? selectedLocationCountryObj.states
    : [];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <SecondTab tabConfig={TabConfig} />
      <PageDescriptionBar items={[
        { type: "icon",  icon: "➕", text: "Add – Create a new company, location or customer" },
        { type: "icon",  icon: "✏️", text: "Edit – Modify company or location details" },
        { type: "separator" },
        { type: "badge", badge: "active",   text: "Active – Location is operational" },
        { type: "badge", badge: "inactive", text: "Inactive – Location is disabled" },
        { type: "separator" },
        { type: "badge", badge: "active",   text: "Approved – Customer can place orders" },
        { type: "badge", badge: "inactive", text: "Unapproved – Customer access is restricted" },
        { type: "separator" },
        { type: "icon",  icon: "🔄", text: "Action Slider – Enable / disable order placement for the customer" },
        { type: "icon",  icon: "📅", text: "Historical Data – Toggle access to historical assay results" },
      ]} />
      <Container fluid className="second-tab-container">
        <div
          className="d-flex align-items-center justify-content-between company-management-tabs"
          style={{ background: "#fff", padding: "8px 0" }}
        >
          <div className="d-flex gap-2">
            <Button
              variant="tab-btn"
              className={`company-tab-btn${
                activeTab === "Company" ? " active" : ""
              }`}
              onClick={() => {
                // switching to Company: clear customer search so we don't keep
                // a lingering customer filter when user returns to that tab later
                setActiveTab("Company");
                setSearch("");
              }}
              style={{ minWidth: 120 }}
              title="View company list"
            >
              Company
            </Button>
            <Button
              variant="tab-btn"
              className={`company-tab-btn${
                activeTab === "Customer" ? " active" : ""
              }`}
              onClick={() => {
                // switching to Customer: clear company search input so Company
                // filter does not persist while viewing customers
                setActiveTab("Customer");
                setSearchInput("");
                // also reset debounced effective search to ensure fetchCompanies
                // picks up the cleared state if needed
                setSearch("");
              }}
              style={{ minWidth: 120 }}
              title="View customer list"
            >
              Customer
            </Button>
          </div>
          {activeTab === "Company" ? (
            <div className="d-flex align-items-center gap-2">
              <select
                className="company-search-input"
                value={companyStatusFilter}
                title="Filter locations by status"
                onChange={(e) => {
                  setCurrentPage(1);
                  setCompanyStatusFilter(e.target.value);
                }}
                style={{
                  width: 110,
                  height: 38,
                  borderRadius: 5,
                  border: "1px solid #bdbdbd",
                  padding: "0 8px",
                }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <Button
                variant="outlined"
                style={{ minWidth: 130 }}
                onClick={() => {
                  setEditMode(null);
                  setCompanyForm({
                    companyName: "",
                    country: "",
                    state: "",
                    city: "",
                  });
                  setShowCompanyModal(true);
                }}
              >
                Add Company
              </Button>
              <Button
                variant="outlined"
                style={{ minWidth: 130 }}
                title="Add a new location"
                onClick={() => {
                  setEditMode(null);
                  setLocationForm({
                    companyName: "",
                    location: "",
                    address: "",
                    country: "",
                    state: "",
                    city: "",
                    zip: "",
                    phone: "",
                    email: "",
                  });
                  setShowLocationModal(true);
                }}
              >
                Add Location
              </Button>
              <input
                type="text"
                className="company-search-input"
                placeholder="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                title="Search companies"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // Immediately apply search on Enter even if short
                    setCurrentPage(1);
                    setSearch(searchInput);
                  }
                }}
                style={{
                  width: 180,
                  height: 38,
                  borderRadius: 5,
                  border: "1px solid #bdbdbd",
                  padding: "0 12px",
                }}
              />
            </div>
          ) : (
            <div
              className="d-flex align-items-center justify-content-end"
              style={{ background: "#fff", padding: "8px 0" }}
            >
              <select
                className="company-search-input"
                value={customerStatusFilter}
                title="Filter customers by status"
                onChange={(e) => setCustomerStatusFilter(e.target.value)}
                style={{
                  width: 110,
                  height: 38,
                  borderRadius: 5,
                  border: "1px solid #bdbdbd",
                  padding: "0 8px",
                  marginRight: 8,
                }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <Button
                variant="outlined"
                style={{ minWidth: 130, marginRight: 8 }}
                onClick={() => setShowCustomerModal(true)}
                title="Add a new customer"
                // Keep enabled when user has an active search — only disable when there
                // are truly no companies and there's no search term.
                disabled={companyLoading || (companyList.length === 0 && !search)}
              >
                Add Customer
              </Button>
              <input
                type="text"
                className="company-search-input"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                title="Search customers"
                style={{
                  width: 180,
                  height: 38,
                  borderRadius: 5,
                  border: "1px solid #bdbdbd",
                  padding: "0 12px",
                }}
              />
            </div>
          )}
        </div>
      </Container>
      <div className="company-management-container">
        {activeTab === "Company" ? (
          <>
            <div className="company-table-section">
              <SimpleTable
                columns={companyColumns}
                data={companyList}
                showPagination
                customRowRender={customRowRender}
                paginationData={paginationData}
                searchValue={search}
                fetchData={fetchCompanies}
              />
            </div>
            <ReusableModal
              show={showCompanyModal}
              onHide={() => {
                setShowCompanyModal(false);
                setEditMode(null);
              }}
              title={editMode === "company" ? "Edit Company" : "Add Company"}
              buttons={[
                {
                  text: "Cancel",
                  variant: "outlined",
                  onClick: () => {
                    setShowCompanyModal(false);
                    setEditMode(null);
                  },
                },
                {
                  text: editMode === "company" ? "Update" : "Add",
                  variant: "active",
                  onClick: async () => {
                    if (formRef.current.validate()) {
                      let isSuccess = false;
                      if (editMode === "company") {
                        isSuccess = await handleUpdateCompany();
                      } else {
                        isSuccess = await handleAddCompany();
                      }
                      if (isSuccess) {
                        setShowCompanyModal(false);
                        setEditMode(null);
                      }
                    }
                  },
                },
              ]}
            >
              <DynamicForm
                initialData={companyForm}
                onChange={(data) => setCompanyForm(data)}
                fields={getCompanyFields(
                  countryStateList,
                  companyStateOptions,
                  phibroRepOptions,
                  companyList.find((c) => c.companyId === companyForm.companyId)
                    ?.locations || [],
                  editMode,
                  companyForm
                )}
                ref={formRef}
                editMode={editMode}
              />
            </ReusableModal>
            <ReusableModal
              show={showLocationModal}
              onHide={() => {
                setShowLocationModal(false);
                setEditMode(null);
              }}
              title={editMode === "location" ? "Edit Location" : "Add Location"}
              buttons={[
                {
                  text: "Cancel",
                  variant: "outlined",
                  onClick: () => {
                    setShowLocationModal(false);
                    setEditMode(null);
                  },
                },
                {
                  text: editMode === "location" ? "Update" : "Add",
                  variant: "active",
                  onClick: () => {
                    if (formRef.current.validate()) {
                      if (editMode === "location") {
                        handleUpdateLocation();
                      } else {
                        handleAddLocation();
                      }
                      setShowLocationModal(false);
                      setEditMode(null);
                    }
                  },
                },
              ]}
            >
              <DynamicForm
                initialData={locationForm}
                onChange={(data) => {
                  setLocationForm(data);
                }}
                fields={(() => {
                  const baseFields = getLocationFields(
                    allCompanies && allCompanies.length > 0 ? allCompanies : companyList,
                    locationStateOptions,
                    countryStateList,
                    phibroRepOptions,
                    companyList.find((c) => c.companyId === locationForm.companyId)
                      ?.locations || [],
                    editMode,
                    locationForm
                  );
                  if (loadingAllCompanies) {
                    return baseFields.map((f) =>
                      f.name === "companyId"
                        ? { ...f, disabled: true, options: [{ label: "Loading...", value: "" }] }
                        : f
                    );
                  }
                  return baseFields;
                })()}
                ref={formRef}
                editMode={editMode}
              />
            </ReusableModal>
          </>
        ) : (
          <CustomerManagement
            companyList={companyList}
            phibroRepOptions={phibroRepOptions}
            showCustomerModal={showCustomerModal}
            setShowCustomerModal={setShowCustomerModal}
            search={search}
            setSearch={setSearch}
            statusFilter={customerStatusFilter}
            isAnalyst={isAnalyst}
            openAddSample={openAddSample}
            onAddSampleOpened={() => {
              setOpenAddSample(false);
              // Clear the query param from the URL
              searchParams.delete("openAddSample");
              setSearchParams(searchParams, { replace: true });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CompanyManagement;